import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { isDevToolsEnabled } from '../constants';
import {
  TEST_EMAIL_TEMPLATES,
  sendSendGridConnectionTest,
  sendCompanyTestEmail,
} from './sendTestEmailsApi';
import '../../demo-appointments/components/DemoAppointmentsPanel.css';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const SendTestEmailsPanel = () => {
  const { companyData, user } = useAuth();
  const isSpecialAdmin = isDevToolsEnabled(user?.isSpecialAdmin);
  const { showToast } = useNotification();
  const [recipient, setRecipient] = useState(() => String(user?.email || '').trim());
  const [templateKey, setTemplateKey] = useState(TEST_EMAIL_TEMPLATES[0].key);
  const [busy, setBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!recipient && user?.email) {
      setRecipient(String(user.email).trim());
    }
  }, [recipient, user?.email]);

  if (!isSpecialAdmin) {
    return null;
  }

  const email = recipient.trim();

  const requireReady = () => {
    if (!isValidEmail(email)) {
      showToast('Enter a valid recipient email.', 'error');
      return false;
    }
    if (!companyData?.id) {
      showToast('Sign in with a company account first.', 'error');
      return false;
    }
    return true;
  };

  const handleConnectionTest = async () => {
    if (!isValidEmail(email)) {
      showToast('Enter a valid recipient email.', 'error');
      return;
    }

    setBusy(true);
    setIsError(false);
    setStatusMessage('Sending SendGrid connection test…');

    try {
      const result = await sendSendGridConnectionTest(email);
      const fromNote = result?.from ? ` From ${result.from}.` : '';
      setStatusMessage(`Connection test sent to ${result?.to || email}.${fromNote}`);
      showToast('Connection test sent', 'success');
    } catch (err) {
      console.error('[SendTestEmailsPanel] connection test failed:', err);
      setIsError(true);
      setStatusMessage(err?.message || 'Failed to send connection test.');
      showToast('Connection test failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSendTemplate = async () => {
    if (!requireReady()) return;

    const template = TEST_EMAIL_TEMPLATES.find((item) => item.key === templateKey);
    setBusy(true);
    setIsError(false);
    setStatusMessage(`Sending ${template?.label || 'sample'} email…`);

    try {
      await sendCompanyTestEmail(companyData.id, templateKey, email);
      setStatusMessage(`${template?.label || 'Sample'} email sent to ${email}.`);
      showToast('Test email sent', 'success');
    } catch (err) {
      console.error('[SendTestEmailsPanel] template send failed:', err);
      setIsError(true);
      setStatusMessage(err?.message || 'Failed to send test email.');
      showToast('Test email failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleSendAll = async () => {
    if (!requireReady()) return;

    setBusy(true);
    setIsError(false);
    setStatusMessage('Sending connection test and sample emails…');

    const sent = [];
    const failed = [];

    try {
      await sendSendGridConnectionTest(email);
      sent.push('Connection test');
    } catch (err) {
      console.error('[SendTestEmailsPanel] connection test failed:', err);
      failed.push(`Connection test (${err?.message || 'failed'})`);
    }

    for (const template of TEST_EMAIL_TEMPLATES) {
      try {
        await sendCompanyTestEmail(companyData.id, template.key, email);
        sent.push(template.label);
      } catch (err) {
        console.error(`[SendTestEmailsPanel] ${template.key} failed:`, err);
        failed.push(`${template.label} (${err?.message || 'failed'})`);
      }
    }

    if (failed.length) {
      setIsError(true);
      setStatusMessage(
        `Sent ${sent.length}. Failed: ${failed.join('; ')}.`
      );
      showToast('Some test emails failed', 'error');
    } else {
      setStatusMessage(`Sent ${sent.length} emails to ${email}.`);
      showToast('All test emails sent', 'success');
    }

    setBusy(false);
  };

  return (
    <section className="demo-appt-panel" aria-labelledby="send-test-emails-title">
      <h3 id="send-test-emails-title" className="demo-appt-panel__title">
        Send test emails
      </h3>
      <p className="demo-appt-panel__desc">
        After SendGrid credentials are saved in the admin panel, send a connection test and sample
        notification emails to confirm outbound mail is working.
      </p>

      <div className="demo-appt-panel__row">
        <div className="demo-appt-panel__field">
          <label htmlFor="dev-test-email-to">Recipient</label>
          <input
            id="dev-test-email-to"
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="you@example.com"
            disabled={busy}
            autoComplete="email"
          />
        </div>
        <div className="demo-appt-panel__field">
          <label htmlFor="dev-test-email-template">Sample template</label>
          <select
            id="dev-test-email-template"
            value={templateKey}
            onChange={(e) => setTemplateKey(e.target.value)}
            disabled={busy}
          >
            {TEST_EMAIL_TEMPLATES.map((template) => (
              <option key={template.key} value={template.key}>
                {template.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="demo-appt-panel__actions">
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--primary"
          onClick={handleSendAll}
          disabled={busy}
        >
          {busy ? 'Sending…' : 'Send all samples'}
        </button>
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--ghost"
          onClick={handleSendTemplate}
          disabled={busy}
        >
          Send selected template
        </button>
        <button
          type="button"
          className="demo-appt-panel__btn demo-appt-panel__btn--ghost"
          onClick={handleConnectionTest}
          disabled={busy}
        >
          Connection test
        </button>
      </div>

      {statusMessage ? (
        <p className={`demo-appt-panel__status${isError ? ' demo-appt-panel__status--error' : ''}`}>
          {statusMessage}
        </p>
      ) : null}

      <p className="demo-appt-panel__hint">
        Uses the platform SendGrid credentials, then this company’s email templates. Only special
        admin accounts can use this tool.
      </p>
    </section>
  );
};

export default SendTestEmailsPanel;
